<!--
  @component OverviewScreen — the project's mission control.
  tier:      screen (per-project — `/projects/:id` and `/overview`)
  reads:     useIdentityIO        → project (name, domain)
             useDeploymentIO      → telemetry.usage (REAL Fly metrics + substrate counts)
             useLiveTimelineEdge  → recent activity
  effects:   navigation + opens the Settings drawer (no tab jump).

  A welcoming home: is my app live, where does it live, how's it doing,
  and what's left to set up. Real metrics where we have them; honest,
  friendly states where we don't. No invented quota ceilings.
-->
<script lang="ts">
  import { push, location as routerLocation } from "svelte-spa-router";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useLiveTimelineEdge } from "../../../../global/io/edges/deployment/LiveTimelineEdge.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import Sparkline from "../../../../global/visuals/primitives/Sparkline.svelte";
  import SetupChecklist, { type SetupStep } from "../../../../global/visuals/primitives/SetupChecklist.svelte";
  import Skeleton from "../../../../global/visuals/primitives/Skeleton.svelte";
  import SettingsDrawer from "./SettingsDrawer.svelte";

  const identity = useIdentityIO();
  const node = useDeploymentIO();
  const timeline = useLiveTimelineEdge();

  const project = $derived(identity.currentProject);
  const liveUrl = $derived(project?.productionDomain ? `https://${project.productionDomain}` : null);

  // ── Real telemetry (polled, accumulated for sparklines) ─────────
  interface TelemetryUsage {
    readonly appExists: boolean;
    readonly compute: { readonly machineCount: number; readonly runningCount: number; readonly memoryAllocatedMb: number };
    readonly metrics?: {
      readonly memoryUsedMb: number | null;
      readonly networkInBytesPerSec: number | null;
      readonly networkOutBytesPerSec: number | null;
      readonly cpuPercent: number | null;
      readonly rps: number | null;
    };
    readonly substrates: { readonly databases: number; readonly buckets: number; readonly caches: number; readonly queues: number };
    readonly history: { readonly releaseCount: number; readonly projectAgeDays: number };
  }
  let usage = $state<TelemetryUsage | null>(null);
  let usageLoading = $state(true);
  let series = $state<{ cpu: number[]; rps: number[]; mem: number[] }>({ cpu: [], rps: [], mem: [] });
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function pushSample(u: TelemetryUsage): void {
    const cap = (a: number[], v: number) => [...a, v].slice(-24);
    series = {
      cpu: cap(series.cpu, u.metrics?.cpuPercent ?? 0),
      rps: cap(series.rps, u.metrics?.rps ?? 0),
      mem: cap(series.mem, u.metrics?.memoryUsedMb ?? 0),
    };
  }

  async function refresh(): Promise<void> {
    const id = project?.id;
    if (!id) { usage = null; usageLoading = false; return; }
    try {
      const u = (await node.dispatch("telemetry.usage", { target: { id } })) as TelemetryUsage;
      usage = u;
      usageLoading = false;
      pushSample(u);
    } catch (e) {
      usageLoading = false;
      // WS opens async — retry once shortly if it raced ahead of OPEN.
      if (e instanceof Error && e.message === "transport not connected" && !retryTimer) {
        retryTimer = setTimeout(() => { retryTimer = null; void refresh(); }, 1000);
      }
    }
  }

  $effect(() => {
    if (!project) return;
    usageLoading = true;
    void refresh();
    const interval = setInterval(() => void refresh(), 8000);
    return () => {
      clearInterval(interval);
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    };
  });

  // ── Status ──────────────────────────────────────────────────────
  type DeployStatus = "live" | "deployed" | "idle";
  const status = $derived<DeployStatus>(
    usage?.appExists && liveUrl ? "live" : (usage?.history.releaseCount ?? 0) > 0 || usage?.appExists ? "deployed" : "idle",
  );
  const STATUS_LABEL: Record<DeployStatus, string> = { live: "Live", deployed: "Deployed", idle: "Not deployed yet" };

  // ── Route (NotFound) ────────────────────────────────────────────
  let routePath = $state("/");
  $effect(() => routerLocation.subscribe((p: string) => (routePath = p)));

  // ── Setup checklist ─────────────────────────────────────────────
  const steps = $derived<SetupStep[]>([
    {
      id: "deploy", label: "Deploy your app",
      hint: "Ship your first release.",
      done: (usage?.history.releaseCount ?? 0) > 0 || (usage?.appExists ?? false),
      actionLabel: "Deploy", onAction: () => go("deployments"),
    },
    {
      id: "domain", label: "Add a custom domain",
      hint: "Give your app a home on the web.",
      done: Boolean(project?.productionDomain),
      actionLabel: "Add", onAction: () => go("domain"),
    },
    {
      id: "data", label: "Set up your data",
      hint: "A database and storage for your app.",
      done: ((usage?.substrates.databases ?? 0) + (usage?.substrates.buckets ?? 0)) > 0,
      actionLabel: "Set up", onAction: () => go("data"),
    },
    {
      id: "team", label: "Invite your team",
      hint: "Collaborate on this project.",
      done: false,
      actionLabel: "Open", onAction: () => (settingsOpen = true),
    },
  ]);

  // ── Resources glance ────────────────────────────────────────────
  const dataCount = $derived((usage?.substrates.databases ?? 0) + (usage?.substrates.buckets ?? 0));
  const toolsCount = $derived((usage?.substrates.caches ?? 0) + (usage?.substrates.queues ?? 0));

  // ── Activity feed ───────────────────────────────────────────────
  const OP_LABEL: Record<string, string> = {
    "release.previewing": "Preview started",
    "release.gated": "Deploy gate checked",
    "release.running": "Deploy running",
    "release.cutover": "Traffic cut over to the new version",
    "release.succeeded": "Deploy succeeded",
    "rollback.restoreToPoint": "Rolled back",
  };
  function activityLabel(op: string): string {
    return OP_LABEL[op] ?? op.replace(/^[a-z]+\./, "").replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
  }
  function timeAgo(ms?: number): string {
    if (!ms) return "";
    const diff = Date.now() - ms;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  }
  const activity = $derived(
    timeline.events.filter((e) => e.op.startsWith("release.") || e.op.startsWith("rollback.")).slice(-6).reverse(),
  );

  // ── Nav ─────────────────────────────────────────────────────────
  let settingsOpen = $state(false);
  let copied = $state(false);
  function go(sub: string): void { if (project) push(`/projects/${project.id}/${sub}`); }
  function openSite(): void { if (liveUrl) window.open(liveUrl, "_blank", "noopener"); }
  async function copyUrl(): Promise<void> {
    if (!liveUrl) return;
    try { await navigator.clipboard.writeText(liveUrl); copied = true; setTimeout(() => (copied = false), 1500); } catch { /* */ }
  }
  function fmt(n: number | null | undefined, unit = "", digits = 0): string {
    if (n === null || n === undefined) return "—";
    return `${n.toFixed(digits)}${unit}`;
  }
  function fmtBytes(bps: number | null | undefined): string {
    if (bps === null || bps === undefined) return "—";
    if (bps < 1024) return `${bps.toFixed(0)} B/s`;
    if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(1)} KB/s`;
    return `${(bps / 1024 / 1024).toFixed(1)} MB/s`;
  }
</script>

{#if !project}
  <div class="notfound">
    <h1>Project not found</h1>
    <p>The link points at <code>{routePath}</code>, but you don't have access to that project.</p>
    <button type="button" class="btn-primary" onclick={() => push("/projects")}>Back to projects</button>
  </div>
{:else}
  <div class="screen">
    <!-- ── Hero ──────────────────────────────────────────────── -->
    <header class="hero">
      <div class="hero-top">
        <div class="hero-id">
          <h1>{project.name}</h1>
          <span class="status {status}">
            <span class="status-dot"></span>{STATUS_LABEL[status]}
          </span>
        </div>
        <div class="hero-actions">
          <button type="button" class="btn-primary" onclick={() => go("deployments")}>Deploy</button>
          {#if liveUrl}<button type="button" class="btn-secondary" onclick={openSite}>Open site</button>{/if}
          <button type="button" class="btn-icon" onclick={() => (settingsOpen = true)} aria-label="Settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </button>
        </div>
      </div>
      {#if liveUrl}
        <div class="url-bar">
          <span class="url-label">Your site is live at</span>
          <button type="button" class="url" onclick={openSite}>{project.productionDomain}</button>
          <button type="button" class="url-copy" onclick={() => void copyUrl()}>{copied ? "Copied" : "Copy"}</button>
        </div>
      {:else}
        <div class="url-bar muted">
          <span class="url-label">No custom domain yet.</span>
          <button type="button" class="url-link" onclick={() => go("domain")}>Add a domain →</button>
        </div>
      {/if}
    </header>

    <SetupChecklist {steps} />

    <!-- ── Metrics glance ─────────────────────────────────────── -->
    <Card eyebrow="Last 8 minutes" title="Performance">
      {#snippet actions()}
        <button type="button" class="link-action" onclick={() => go("performance")}>View performance →</button>
      {/snippet}
      {#if usageLoading}
        <div class="metrics">
          {#each Array.from({ length: 4 }) as _, i (i)}
            <div class="metric"><Skeleton variant="line" width="40%" height="11px" /><Skeleton variant="line" width="60%" height="20px" /><Skeleton variant="block" height="32px" /></div>
          {/each}
        </div>
      {:else}
        <div class="metrics">
          <div class="metric">
            <span class="m-label">Machines</span>
            <span class="m-value">{usage?.compute.runningCount ?? 0}<span class="m-unit"> / {usage?.compute.machineCount ?? 0}</span></span>
            <span class="m-spark"></span>
          </div>
          <div class="metric">
            <span class="m-label">CPU</span>
            <span class="m-value">{fmt(usage?.metrics?.cpuPercent, "%", 0)}</span>
            <span class="m-spark"><Sparkline values={series.cpu} color="var(--text-secondary)" /></span>
          </div>
          <div class="metric">
            <span class="m-label">Memory</span>
            <span class="m-value">{fmt(usage?.metrics?.memoryUsedMb, " MB", 0)}</span>
            <span class="m-spark"><Sparkline values={series.mem} color="var(--text-secondary)" /></span>
          </div>
          <div class="metric">
            <span class="m-label">Requests / sec</span>
            <span class="m-value">{fmt(usage?.metrics?.rps, "", 1)}</span>
            <span class="m-spark"><Sparkline values={series.rps} color="var(--text-secondary)" /></span>
          </div>
        </div>
        <div class="net">
          <span>↓ {fmtBytes(usage?.metrics?.networkInBytesPerSec)} in</span>
          <span>↑ {fmtBytes(usage?.metrics?.networkOutBytesPerSec)} out</span>
        </div>
      {/if}
    </Card>

    <!-- ── Resources + Activity ───────────────────────────────── -->
    <div class="cols">
      <Card eyebrow="What this app uses" title="Resources">
        <button type="button" class="res" onclick={() => go("data")}>
          <span class="res-name">Data</span>
          <span class="res-state">{dataCount > 0 ? `${dataCount} provisioned` : "Set up"}</span>
        </button>
        <button type="button" class="res" onclick={() => go("performance")}>
          <span class="res-name">Performance tools</span>
          <span class="res-state">{toolsCount > 0 ? `${toolsCount} provisioned` : "Set up"}</span>
        </button>
        <button type="button" class="res" onclick={() => go("domain")}>
          <span class="res-name">Domain</span>
          <span class="res-state">{project.productionDomain ?? "Set up"}</span>
        </button>
      </Card>

      <Card eyebrow="What's happened" title="Recent activity">
        {#snippet actions()}
          <button type="button" class="link-action" onclick={() => go("logs")}>All logs →</button>
        {/snippet}
        {#if activity.length === 0}
          <p class="empty">No activity yet. Your deploys and changes will show up here.</p>
        {:else}
          <ul class="activity">
            {#each activity as e (e.atMs + e.op)}
              <li><span class="a-label">{activityLabel(e.op)}</span><span class="a-time">{timeAgo(e.atMs)}</span></li>
            {/each}
          </ul>
        {/if}
      </Card>
    </div>
  </div>

  <SettingsDrawer open={settingsOpen} onClose={() => (settingsOpen = false)} />
{/if}

<style>
  .screen { display: flex; flex-direction: column; gap: var(--space-5); max-width: 1100px; font-family: var(--type-font); }

  .notfound { max-width: 460px; display: flex; flex-direction: column; gap: var(--space-4); }
  .notfound h1 { margin: 0; font-size: 20px; font-weight: 600; color: var(--text-primary); }
  .notfound p { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.6; }
  .notfound code { font-family: var(--type-mono); font-size: 12px; color: var(--text-secondary); }

  /* Hero */
  .hero { display: flex; flex-direction: column; gap: var(--space-4); }
  .hero-top { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); }
  .hero-id { display: flex; align-items: center; gap: var(--space-3); }
  .hero-id h1 { margin: 0; font-size: 26px; font-weight: 600; letter-spacing: -0.025em; color: var(--text-primary); }
  .status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; padding: 4px var(--space-3); border-radius: var(--radius-pill); border: 0.5px solid var(--border); color: var(--text-secondary); }
  .status-dot { width: 6px; height: 6px; border-radius: var(--radius-pill); background: var(--text-tertiary); }
  .status.live { color: var(--success); border-color: var(--success-subtle); }
  .status.live .status-dot { background: var(--success); }
  .status.deployed .status-dot { background: var(--text-secondary); }

  .hero-actions { display: flex; align-items: center; gap: var(--space-2); }
  .btn-primary, .btn-secondary { appearance: none; border-radius: var(--radius-md); padding: 0 var(--space-4); height: 36px; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .btn-primary { background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); }
  .btn-primary:hover { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-secondary { background: transparent; color: var(--text-secondary); border: 0.5px solid var(--border); }
  .btn-secondary:hover { background: var(--bg-hover); color: var(--text-primary); }
  .btn-icon { width: 36px; height: 36px; display: grid; place-items: center; background: transparent; border: 0.5px solid var(--border); border-radius: var(--radius-md); color: var(--icon); cursor: pointer; transition: background var(--ease-fast), color var(--ease-fast); }
  .btn-icon:hover { background: var(--bg-hover); color: var(--icon-hover); }
  .btn-icon svg { width: 16px; height: 16px; }

  .url-bar { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-3) var(--space-4); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-md); }
  .url-label { font-size: 12.5px; color: var(--text-tertiary); }
  .url { background: transparent; border: 0; color: var(--text-primary); font: inherit; font-size: 14px; font-weight: 500; cursor: pointer; padding: 0; }
  .url:hover { text-decoration: underline; }
  .url-copy { margin-left: auto; background: transparent; border: 0.5px solid var(--border-subtle); border-radius: var(--radius-sm); color: var(--text-tertiary); font: inherit; font-size: 11px; padding: 3px var(--space-2); cursor: pointer; }
  .url-copy:hover { color: var(--text-primary); border-color: var(--border); }
  .url-link { background: transparent; border: 0; color: var(--text-secondary); font: inherit; font-size: 13px; cursor: pointer; padding: 0; }
  .url-link:hover { color: var(--text-primary); }

  /* Metrics */
  .link-action { background: transparent; border: 0; color: var(--text-secondary); font: inherit; font-size: 12.5px; cursor: pointer; }
  .link-action:hover { color: var(--text-primary); }
  .metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-4); }
  .metric { display: flex; flex-direction: column; gap: var(--space-1); }
  .m-label { font-size: 11.5px; color: var(--text-tertiary); }
  .m-value { font-size: 20px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .m-unit { font-size: 13px; font-weight: 400; color: var(--text-tertiary); }
  .m-spark { height: 32px; display: block; }
  .net { display: flex; gap: var(--space-5); margin-top: var(--space-4); padding-top: var(--space-3); border-top: 0.5px solid var(--border-subtle); font-size: 12px; color: var(--text-tertiary); }

  /* Resources + activity */
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); }
  .res { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); width: 100%; padding: var(--space-3) 0; background: transparent; border: 0; border-top: 0.5px solid var(--border-subtle); font: inherit; cursor: pointer; text-align: left; }
  .res:first-child { border-top: 0; }
  .res-name { font-size: 13.5px; color: var(--text-primary); }
  .res-state { font-size: 12.5px; color: var(--text-tertiary); }
  .res:hover .res-state { color: var(--text-secondary); }

  .empty { margin: 0; font-size: 13px; color: var(--text-tertiary); line-height: 1.6; }
  .activity { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
  .activity li { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: var(--space-2) 0; border-top: 0.5px solid var(--border-subtle); }
  .activity li:first-child { border-top: 0; }
  .a-label { font-size: 13px; color: var(--text-secondary); }
  .a-time { font-size: 12px; color: var(--text-ghost); flex-shrink: 0; }

  @media (max-width: 880px) { .metrics { grid-template-columns: repeat(2, 1fr); } .cols { grid-template-columns: 1fr; } }
</style>
